/**
 * Shared dependencies
 */
import type { ValioAppStore } from '../../shared/types/global';

/**
 * Waits for the Zustand store to be initialized on window.valioApp
 * This is useful when you need to access the store before it's guaranteed to be ready
 *
 * @param timeout - Maximum time to wait in milliseconds (default: 5000ms)
 * @returns Promise that resolves with the store when it's ready
 * @throws Error if store is not initialized within the timeout period
 */
export function waitForStore(timeout: number = 5000): Promise<ValioAppStore> {
	// If store is already available, return it immediately
	if (window.valioApp?.store) {
		return Promise.resolve(window.valioApp.store);
	}

	return new Promise((resolve, reject) => {
		const timeoutId = setTimeout(() => {
			reject(new Error('Store initialization timed out'));
		}, timeout);

		// Listen for the custom event dispatched when store is ready
		const handleStoreReady = (event: CustomEvent) => {
			clearTimeout(timeoutId);
			window.removeEventListener('valioStoreReady', handleStoreReady as EventListener);
			resolve(event.detail.store as ValioAppStore);
		};

		window.addEventListener('valioStoreReady', handleStoreReady as EventListener);

		// Check again in case the event was dispatched before we started listening
		if (window.valioApp?.store) {
			clearTimeout(timeoutId);
			window.removeEventListener('valioStoreReady', handleStoreReady as EventListener);
			resolve(window.valioApp.store as ValioAppStore);
		}
	});
}

/**
 * Gets the store if it's available, otherwise returns null
 * Use this when you want to check for the store without waiting
 *
 * @returns The store if available, null otherwise
 */
export function getStoreIfReady(): ValioAppStore | null {
	return window.valioApp?.store || null;
}
